import React, { useRef, useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';

export function PaymentForm({ session, onStart, onComplete, onBusyChange }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [setup, setSetup] = useState(session);
  const [saved, setSaved] = useState(session?.status === 'succeeded');
  const [ready, setReady] = useState(false);
  const inFlight = useRef(false);
  const submit = async event => {
    event.preventDefault();
    if (inFlight.current || (!saved && (!stripe || !elements || !ready))) return;
    inFlight.current = true; setBusy(true); onBusyChange?.(true); setError('');
    try {
      let current = setup;
      if (!saved) {
        const validation = await elements.submit();
        if (validation.error) { setError(validation.error.message); return; }
        if (!current) {
          current = await onStart();
          if (current.active) return;
          setSetup(current);
        }
        if (current.status !== 'succeeded') {
          const result = await stripe.confirmSetup({ elements, ...(!session ? { clientSecret: current.client_secret } : {}),
            confirmParams: { return_url: `${window.location.origin}/billing-payment` }, redirect: 'if_required' });
          if (result.error) {
            setError(result.error.type === 'card_error' || result.error.type === 'validation_error'
              ? result.error.message : 'Unable to save this payment method. Please try again.');
            return;
          }
          if (result.setupIntent?.status !== 'succeeded') { setError('Payment setup is not complete yet. Please retry after verification.'); return; }
        }
        setSaved(true);
      }
      await onComplete(current.setup_id);
    } catch { setError('Unable to finish account setup. Retry to check your saved payment method and activate your account.'); }
    finally { inFlight.current = false; setBusy(false); onBusyChange?.(false); }
  };
  return <Box component="form" onSubmit={submit}>
    <Stack spacing={2}>
      {saved ? <Alert severity="success">Your payment method is saved. Finish activating your account.</Alert> :
        <PaymentElement onReady={() => setReady(true)} onLoadError={() => setError('The payment form could not load. Reload this page to resume setup.')} />}
      {error && <Alert severity="error">{error}</Alert>}
      <Button type="submit" variant="contained" disabled={busy || (!saved && (!stripe || !elements || !ready))}>
        {busy ? 'Finishing setup…' : saved ? 'Activate account' : 'Save payment method and finish'}
      </Button>
      <Typography variant="body2" color="text.secondary">No payment is collected today. Stripe securely saves your card for monthly billing.</Typography>
    </Stack>
  </Box>;
}

export function SavedSetup({ session, onComplete }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inFlight = useRef(false);
  return <Stack spacing={2}><Alert severity="success">Your payment method is saved. Finish activating your account.</Alert>
    {error && <Alert severity="error">{error}</Alert>}
    <Button variant="contained" disabled={busy} onClick={async () => {
      if (inFlight.current) return;
      inFlight.current = true; setBusy(true); setError('');
      try { await onComplete(session.setup_id); } catch (failure) { setError(failure.message); }
      finally { inFlight.current = false; setBusy(false); }
    }}>{busy ? 'Activating…' : 'Activate account'}</Button></Stack>;
}
